import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  BugReport as BugReportIcon,
  Help as HelpIcon,
  Build as BuildIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const SupportPage: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    // Frontend validation
    if (formData.subject.trim().length < 1) {
      setError('Naslov ne može biti prazan');
      setSubmitting(false);
      return;
    }

    if (formData.description.trim().length < 1) {
      setError('Opis ne može biti prazan');
      setSubmitting(false);
      return;
    }

    try {
      await api.post('/support/ticket', formData);

      setSuccess(true);
      setFormData({
        subject: '',
        category: 'general',
        priority: 'medium',
        description: '',
      });
    } catch (err: any) {
      setError(err.message || 'Greška pri slanju zahteva. Pokušajte ponovo ili nas kontaktirajte direktno.');
    } finally {
      setSubmitting(false);
    }
  };

  const supportCategories = [
    { value: 'general', label: 'Opšti upit', icon: <HelpIcon /> },
    { value: 'technical', label: 'Tehnički problem', icon: <BuildIcon /> },
    { value: 'bug', label: 'Prijava greške', icon: <BugReportIcon /> },
    { value: 'billing', label: 'Naplata i pretplata', icon: <BusinessIcon /> },
  ];

  const priorities = [
    { value: 'low', label: 'Nizak', color: 'info' as const },
    { value: 'medium', label: 'Srednji', color: 'warning' as const },
    { value: 'high', label: 'Visok', color: 'error' as const },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Podrška
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Tu smo da vam pomognemo! Kontaktirajte naš support tim za bilo kakvu pomoć.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Kontakt Informacije
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Email"
                  secondary="office@omnipass.rs"
                  secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Telefon"
                  secondary="+381 11 123 4567"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <AccessTimeIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Radno vreme"
                  secondary="Pon-Pet: 09:00 - 17:00"
                />
              </ListItem>
            </List>
          </Paper>

          {/* Quick Tips */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Česta Pitanja
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Kako kreirati novi pass?"
                  secondary="Pass Manager → Novi Pass"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Kako podesiti API ključ?"
                  secondary="Account → API Ključevi"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Kako videti statistiku?"
                  secondary="Dashboard → Analytics"
                />
              </ListItem>
            </List>

            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              href="https://docs.omnipass.rs"
              target="_blank"
            >
              Dokumentacija
            </Button>
          </Paper>
        </Grid>

        {/* Support Ticket Form */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Kreiraj Support Ticket
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Popunite formu ispod i naš tim će vam odgovoriti u najkraćem roku.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
                ✅ Vaš zahtev je uspešno poslat! Odgovorićemo vam na email: {user?.email}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Naslov"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Ukratko opišite problem..."
                    helperText="Email će imati prefix 'Support:' + vaš tekst"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Kategorija"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                  >
                    {supportCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Prioritet"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                  >
                    {priorities.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Opis problema"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Detaljno opišite problem ili pitanje..."
                    helperText="Kategorija i prioritet će biti vidljivi u email-u"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box display="flex" gap={2} alignItems="center">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={
                        submitting ||
                        formData.subject.trim().length < 1 ||
                        formData.description.trim().length < 1
                      }
                      startIcon={<EmailIcon />}
                    >
                      {submitting ? 'Šaljem...' : 'Pošalji Zahtev'}
                    </Button>

                    <Typography variant="body2" color="textSecondary">
                      Odgovor ćete dobiti na: <strong>{user?.email}</strong>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </form>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                💡 <strong>Saveti:</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" component="ul" sx={{ pl: 2 }}>
                <li>Budite što detaljniji u opisu problema</li>
                <li>Priložite screenshot ako je moguće (možete poslati naknadno na email)</li>
                <li>Navedite korake za reprodukciju greške</li>
                <li>Uključite relevantne error poruke</li>
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SupportPage;
